import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  avaliacaoPublica,
  ApiError,
  type FormularioAvaliacao as Dados,
  type AvaliacaoRealizada,
} from "../api";
import { AvaliacaoItem } from "./AvaliacoesColaboradorModal";
import FormularioAvaliacao, {
  EscalaNiveis,
  RESPOSTAS_VAZIAS,
  mediaDasNotas,
  nivelDe,
  type RespostasFormulario,
} from "./FormularioAvaliacao";
import { IconStar, IconCheck, IconSave } from "../icons";
import { ordenarPorNome } from "../ordem";

/**
 * Página PÚBLICA (sem login) que o gestor abre pelo link do RH para avaliar a
 * equipe — agora com o formulário OFICIAL da SERCAMP (FO 094): para cada
 * colaborador aparecem as perguntas do CARGO dele (gerais + específicas), a
 * escala de 1 a 10 e os campos de pontos fortes, melhorias e observações.
 *
 * A nota final de cada um é a média das questões respondidas, calculada na
 * hora — igual à planilha, onde o total é fórmula.
 *
 * PREENCHER EM VÁRIAS SENTADAS: cada colaborador tem o próprio botão SALVAR.
 * O que é salvo fica guardado no servidor como rascunho e o link continua
 * pendente — o gestor fecha o navegador, volta pelo mesmo endereço e a página
 * reabre no ponto onde parou (já pulando quem foi preenchido). Antes era tudo
 * ou nada: quem parava no meio recomeçava do zero na visita seguinte. Só o
 * "Concluir e enviar" fecha o formulário e manda as avaliações ao RH.
 */

/** Situação do rascunho de UM colaborador (o selo ao lado do nome). */
type EstadoSalvo = "editando" | "salvando" | "salvo" | "erro";

/** Só salva quem tem alguma coisa preenchida. */
function temConteudo(r: RespostasFormulario | undefined): boolean {
  if (!r) return false;
  return (
    Object.values(r.notas ?? {}).some((v) => String(v).trim() !== "") ||
    r.pontos_fortes.trim() !== "" ||
    r.pontos_melhoria.trim() !== "" ||
    r.observacoes.trim() !== ""
  );
}

/** "14:32" — a hora do último salvamento, para o gestor confiar no que vê. */
function hora(iso: string | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}

/**
 * Moldura da página (cabeçalho, cartão e rodapé).
 *
 * Fica AQUI FORA de propósito — e não dentro do componente, como já esteve.
 * Declarada dentro, ela virava uma função NOVA a cada tecla digitada; o React
 * compara o tipo do elemento por identidade, via um componente diferente e
 * DESMONTAVA a página inteira a cada letra: o campo perdia o foco e a rolagem
 * voltava para o topo ("a página sobe sozinha e não deixa escrever").
 * As outras páginas públicas (ClimaPublica, TriagemPublica) já faziam assim.
 */
function Casca({ codigo, children }: { codigo?: string | null; children: React.ReactNode }) {
  return (
    <div className="aval-publica">
      <div className="aval-header">
        <span className="aval-brand">SERCAMP</span>
        <span className="aval-brand-sub">
          Avaliação de Desempenho{codigo ? ` · ${codigo}` : ""}
        </span>
      </div>
      <div className="aval-card">{children}</div>
      <p className="aval-rodape">RH SERCAMP · preenchimento seguro por link</p>
    </div>
  );
}

/** O selo de "salvo / salvando / não salvo" que aparece na linha da pessoa. */
function SeloRascunho({ estado, quando }: { estado?: EstadoSalvo; quando?: string }) {
  if (estado === "salvando") return <span className="aval-selo salvando">Salvando…</span>;
  if (estado === "erro") return <span className="aval-selo erro">Não salvou</span>;
  if (estado === "salvo")
    return (
      <span className="aval-selo ok">
        <IconCheck size={12} /> Salvo{quando ? ` ${quando}` : ""}
      </span>
    );
  if (estado === "editando") return <span className="aval-selo pendente">Não salvo</span>;
  return null;
}

/**
 * Depois de enviar (ou ao reabrir um link já respondido), o gestor vê AQUI o
 * que ele preencheu — questão a questão. Antes o link de uso único só dizia
 * "já foi respondido" e ele ficava sem cópia do que avaliou.
 */
function EnviadasDoGestor({ token }: { token: string }) {
  const [dados, setDados] = useState<{
    link: { gestor_nome: string | null; departamento: string | null; respondido_em: string | null };
    avaliacoes: AvaliacaoRealizada[];
  } | null>(null);
  const [falhou, setFalhou] = useState(false);
  const [abertaId, setAbertaId] = useState<number | null>(null);

  useEffect(() => {
    let vivo = true;
    avaliacaoPublica
      .enviadas(token)
      .then((d) => vivo && setDados(d))
      .catch(() => vivo && setFalhou(true));
    return () => {
      vivo = false;
    };
  }, [token]);

  // Falha aqui não é erro de verdade: o envio já foi confirmado acima. Some
  // em silêncio em vez de assustar o gestor com uma mensagem vermelha.
  if (falhou || !dados || dados.avaliacoes.length === 0) return null;

  return (
    <div className="aval-enviadas avaliacoes-historico">
      <h3>O que você avaliou ({dados.avaliacoes.length})</h3>
      <p className="aval-enviadas-dica">
        Esta é a sua cópia. O link continua abrindo esta página — guarde-o se quiser conferir depois.
      </p>
      {/* Uma avaliação por pessoa do mesmo envio: em ordem alfabética. */}
      {ordenarPorNome(dados.avaliacoes, (a) => a.colaborador_nome).map((a) => (
        <AvaliacaoItem
          key={a.id}
          avaliacao={a}
          aberta={abertaId === a.id}
          onToggle={() => setAbertaId((atual) => (atual === a.id ? null : a.id))}
          titulo={a.colaborador_nome ?? undefined}
        />
      ))}
    </div>
  );
}

export default function AvaliacaoPublica({ token }: { token: string }) {
  const [form, setForm] = useState<Dados | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [respostas, setRespostas] = useState<Record<number, RespostasFormulario>>({});
  /** Formulário escolhido por colaborador (o do cargo vem marcado). */
  const [modelos, setModelos] = useState<Record<number, string>>({});
  const [aberto, setAberto] = useState<number | null>(null);
  const [periodo, setPeriodo] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const [erroEnvio, setErroEnvio] = useState<string | null>(null);
  /** Rascunho de cada colaborador: em que pé está e de quando é. */
  const [estados, setEstados] = useState<Record<number, EstadoSalvo>>({});
  const [salvoEm, setSalvoEm] = useState<Record<number, string>>({});
  const [avisoSalvar, setAvisoSalvar] = useState<string | null>(null);

  /**
   * O estado mais recente para quem salva fora do render: o autossalvamento
   * dispara por temporizador e não pode enviar um retrato velho da tela.
   */
  const dadosRef = useRef({ respostas, modelos, periodo });
  useEffect(() => {
    dadosRef.current = { respostas, modelos, periodo };
  }, [respostas, modelos, periodo]);
  /** Idem para o "já salvei este antes?" lido dentro do salvamento. */
  const salvoEmRef = useRef(salvoEm);
  useEffect(() => {
    salvoEmRef.current = salvoEm;
  }, [salvoEm]);

  /** Quem mudou desde o último salvamento (fila do autossalvamento). */
  const pendentesRef = useRef<Set<number>>(new Set());
  const salvandoRef = useRef(false);
  /** Sobe a cada tecla: é o que reinicia a contagem do autossalvamento. */
  const [marca, setMarca] = useState(0);

  useEffect(() => {
    let vivo = true;
    avaliacaoPublica
      .obter(token)
      .then((recebido) => {
        if (!vivo) return;
        // Os avaliados em ordem alfabética (src/ordem.ts) — é também a ordem
        // do "primeiro que falta" aberto lá embaixo.
        const f = {
          ...recebido,
          colaboradores: ordenarPorNome(recebido.colaboradores, (c) => c.nome),
        };
        setForm(f);
        setPeriodo(f.periodo ?? "");
        const iniciais: Record<number, RespostasFormulario> = {};
        const escolhidos: Record<number, string> = {};
        for (const c of f.colaboradores) {
          iniciais[c.id] = { ...RESPOSTAS_VAZIAS, notas: {} };
          if (c.modelo) escolhidos[c.id] = c.modelo;
        }

        // O QUE JÁ FOI SALVO volta para a tela: é isto que faz o gestor
        // continuar de onde parou em vez de responder tudo de novo.
        const jaSalvos: Record<number, EstadoSalvo> = {};
        const horas: Record<number, string> = {};
        for (const r of f.rascunhos ?? []) {
          if (!iniciais[r.colaborador_id]) continue;
          iniciais[r.colaborador_id] = {
            notas: { ...(r.respostas ?? {}) },
            pontos_fortes: r.pontos_fortes ?? "",
            pontos_melhoria: r.pontos_melhoria ?? "",
            observacoes: r.observacoes ?? "",
          };
          if (r.modelo) escolhidos[r.colaborador_id] = r.modelo;
          jaSalvos[r.colaborador_id] = "salvo";
          horas[r.colaborador_id] = r.atualizado_em;
        }

        setRespostas(iniciais);
        setModelos(escolhidos);
        setEstados(jaSalvos);
        setSalvoEm(horas);
        // Abre o PRIMEIRO QUE FALTA (numa lista de 10 pessoas com 20 perguntas
        // cada, tudo aberto de uma vez é rolagem infinita — e reabrir em quem
        // já está salvo faria o gestor procurar de novo onde tinha parado).
        const proximo = f.colaboradores.find((c) => !jaSalvos[c.id]) ?? f.colaboradores[0];
        setAberto(proximo?.id ?? null);
      })
      .catch((e) => vivo && setErro(e instanceof ApiError ? e.message : "Link inválido."))
      .finally(() => vivo && setCarregando(false));
    return () => {
      vivo = false;
    };
  }, [token]);

  const modeloDe = useMemo(
    () => (id: number) => form?.modelos.find((m) => m.key === modelos[id]) ?? null,
    [form, modelos],
  );

  /** Manda UM colaborador para o servidor (o botão Salvar e o automático). */
  const salvarUm = useCallback(
    async (id: number, manual = false) => {
      const { respostas: rs, modelos: ms, periodo: p } = dadosRef.current;
      const r = rs[id];
      // Nada preenchido e nada salvo antes: não há o que guardar.
      if (!temConteudo(r) && !salvoEmRef.current[id]) {
        pendentesRef.current.delete(id);
        if (manual) setAvisoSalvar("Preencha alguma nota ou comentário antes de salvar.");
        return false;
      }
      pendentesRef.current.delete(id);
      setEstados((e) => ({ ...e, [id]: "salvando" }));
      try {
        const resp = await avaliacaoPublica.salvarRascunho(token, {
          colaborador_id: id,
          modelo: ms[id] ?? null,
          respostas: r?.notas ?? {},
          pontos_fortes: r?.pontos_fortes ?? "",
          pontos_melhoria: r?.pontos_melhoria ?? "",
          comentario: r?.observacoes ?? "",
          periodo: p.trim() || undefined,
        });
        setEstados((e) => ({ ...e, [id]: "salvo" }));
        setSalvoEm((s) => ({ ...s, [id]: new Date().toISOString() }));
        setAvisoSalvar(resp.aviso ?? null);
        return true;
      } catch (e) {
        // Volta para a fila: a próxima tentativa (ou o botão) reenvia.
        pendentesRef.current.add(id);
        setEstados((anterior) => ({ ...anterior, [id]: "erro" }));
        setAvisoSalvar(
          e instanceof ApiError
            ? e.message
            : "Sem conexão para salvar agora. Não feche a página — vamos tentar de novo.",
        );
        return false;
      }
    },
    [token],
  );

  /** Salva todo mundo que está na fila, um de cada vez. */
  const descarregarFila = useCallback(async () => {
    if (salvandoRef.current) return;
    salvandoRef.current = true;
    try {
      for (const id of [...pendentesRef.current]) {
        await salvarUm(id);
      }
    } finally {
      salvandoRef.current = false;
    }
  }, [salvarUm]);

  /** Marca o colaborador como alterado e reinicia a contagem do salvamento. */
  function tocar(id: number) {
    pendentesRef.current.add(id);
    setEstados((e) => (e[id] === "editando" ? e : { ...e, [id]: "editando" }));
    setMarca((m) => m + 1);
  }

  // Autossalvamento: 2,5 s parado depois da última tecla e o rascunho sobe.
  // Existe para o caso pior — a pessoa fecha o navegador sem clicar em nada.
  useEffect(() => {
    if (marca === 0 || enviado) return;
    const t = setTimeout(() => {
      void descarregarFila();
    }, 2500);
    return () => clearTimeout(t);
  }, [marca, enviado, descarregarFila]);

  // Aviso do navegador se ainda houver coisa não salva (rede fora, por ex.).
  useEffect(() => {
    const antes = (e: BeforeUnloadEvent) => {
      if (enviado || pendentesRef.current.size === 0) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", antes);
    return () => window.removeEventListener("beforeunload", antes);
  }, [enviado]);

  /** Abrir outra pessoa salva a que estava aberta — sem clique extra. */
  function abrir(id: number | null) {
    const anterior = aberto;
    setAberto(id);
    if (anterior != null && anterior !== id && pendentesRef.current.has(anterior)) {
      void salvarUm(anterior);
    }
  }

  const preenchidas = form
    ? form.colaboradores.filter((c) => temConteudo(respostas[c.id])).length
    : 0;
  const salvos = form ? form.colaboradores.filter((c) => estados[c.id] === "salvo").length : 0;

  async function enviar() {
    if (!form) return;
    setErroEnvio(null);
    if (preenchidas === 0) {
      setErroEnvio("Preencha ao menos as notas (ou os comentários) de um colaborador.");
      return;
    }
    // Enviar ENCERRA o formulário: o link vira "Respondido" e não abre mais.
    // Quem ainda tem gente para avaliar deve salvar e voltar depois.
    const faltam = form.colaboradores.length - preenchidas;
    if (faltam > 0) {
      const ok = window.confirm(
        `Faltam ${faltam} colaborador(es) sem preenchimento.\n\n` +
          "Ao enviar, o formulário é encerrado e o link não abre mais. " +
          "Se quiser terminar depois, cancele este aviso e use o botão Salvar de cada pessoa — " +
          "o link continua valendo e você volta de onde parou.\n\nEnviar assim mesmo?",
      );
      if (!ok) return;
    }

    const avaliacoes = form.colaboradores.map((c) => ({
      colaborador_id: c.id,
      modelo: modelos[c.id] ?? null,
      respostas: respostas[c.id]?.notas ?? {},
      pontos_fortes: respostas[c.id]?.pontos_fortes ?? "",
      pontos_melhoria: respostas[c.id]?.pontos_melhoria ?? "",
      comentario: respostas[c.id]?.observacoes ?? "",
    }));
    setEnviando(true);
    try {
      pendentesRef.current.clear();
      await avaliacaoPublica.enviar(token, { periodo: periodo.trim() || undefined, avaliacoes });
      setEnviado(true);
    } catch (e) {
      setErroEnvio(e instanceof ApiError ? e.message : "Falha ao enviar. Tente novamente.");
    } finally {
      setEnviando(false);
    }
  }

  const codigo = form?.codigo_formulario;

  if (carregando)
    return (
      <Casca codigo={codigo}>
        <div className="empty">Carregando…</div>
      </Casca>
    );

  if (erro)
    return (
      <Casca codigo={codigo}>
        <div className="aval-estado">
          <h2>Link indisponível</h2>
          <p>{erro}</p>
        </div>
      </Casca>
    );

  if (enviado || form?.respondido)
    return (
      <Casca codigo={codigo}>
        <div className="aval-estado aval-ok">
          <div className="aval-ok-icon">
            <IconCheck size={40} />
          </div>
          <h2>Avaliação registrada!</h2>
          <p>Obrigado. As avaliações do setor {form?.departamento} foram enviadas ao RH com sucesso.</p>
        </div>
        <EnviadasDoGestor token={token} />
      </Casca>
    );

  if (form?.expirado)
    return (
      <Casca codigo={codigo}>
        <div className="aval-estado">
          <h2>Link expirado</h2>
          <p>Este link de avaliação expirou. Solicite um novo ao RH.</p>
        </div>
      </Casca>
    );

  if (!form || form.colaboradores.length === 0)
    return (
      <Casca codigo={codigo}>
        <div className="aval-estado">
          <h2>Sem colaboradores</h2>
          <p>Não há colaboradores ativos no setor {form?.departamento} para avaliar no momento.</p>
        </div>
      </Casca>
    );

  return (
    <Casca codigo={codigo}>
      <div className="aval-intro">
        <p>
          Olá, <strong>{form.gestor_nome}</strong>. Avalie o desempenho da equipe do setor{" "}
          <strong>{form.departamento}</strong>. Cada colaborador tem o formulário da própria função —
          dê uma nota de <strong>1 a 10</strong> em cada questão; a nota final é a média.
        </p>
        <p className="aval-dica">
          <IconSave size={15} /> <strong>Não precisa fazer tudo de uma vez.</strong> Salve cada
          colaborador que terminar (o botão fica na linha da pessoa) e feche a página quando quiser.
          Ao voltar por este mesmo link, o que você salvou estará aqui e você continua de onde parou.
          O RH só recebe as avaliações quando você clicar em <strong>Concluir e enviar</strong>.
        </p>
        <ul className="aval-instrucoes">
          {form.instrucoes.map((i) => (
            <li key={i}>{i}</li>
          ))}
        </ul>
        <EscalaNiveis niveis={form.niveis} />
        <label className="field aval-periodo">
          <span>Período</span>
          <input
            value={periodo}
            onChange={(e) => {
              setPeriodo(e.target.value);
              if (aberto != null) tocar(aberto);
            }}
            placeholder="Ex.: 2026 - 1º semestre"
          />
        </label>
      </div>

      <div className="aval-lista">
        {form.colaboradores.map((c) => {
          const r = respostas[c.id] ?? RESPOSTAS_VAZIAS;
          const media = mediaDasNotas(r.notas);
          const estaAberto = aberto === c.id;
          const respondidas = Object.values(r.notas ?? {}).filter(
            (v) => String(v).trim() !== "",
          ).length;
          const totalQuestoes = modeloDe(c.id)?.questoes.length ?? c.questoes.length;
          return (
            <div
              className={`aval-item ${estaAberto ? "aberto" : ""} ${
                estados[c.id] === "salvo" ? "salvo" : ""
              }`}
              key={c.id}
            >
              <div className="aval-item-topo">
                <button
                  type="button"
                  className="aval-item-head"
                  onClick={() => abrir(estaAberto ? null : c.id)}
                >
                  <span className="aval-nome">{c.nome}</span>
                  {c.cargo && <span className="aval-cargo">{c.cargo}</span>}
                  <span className="aval-qtd">
                    {totalQuestoes > 0
                      ? `${respondidas} de ${totalQuestoes} questões`
                      : "escolher formulário"}
                  </span>
                  <span className="aval-media" title="Nota final (média das questões)">
                    <IconStar size={13} /> {media != null ? media.toFixed(1) : "—"}
                    <small>{nivelDe(media, form.niveis)}</small>
                  </span>
                </button>
                {/* O botão de salvar fica FORA do cabeçalho: um <button> dentro
                    de outro é HTML inválido e o clique viraria o do accordion. */}
                <div className="aval-item-acoes">
                  <SeloRascunho estado={estados[c.id]} quando={hora(salvoEm[c.id])} />
                  <button
                    type="button"
                    className="aval-salvar"
                    title={`Salvar a avaliação de ${c.nome} e continuar depois`}
                    onClick={() => void salvarUm(c.id, true)}
                    disabled={estados[c.id] === "salvando"}
                  >
                    <IconSave size={15} />
                    <span>{estados[c.id] === "salvando" ? "Salvando…" : "Salvar"}</span>
                  </button>
                </div>
              </div>

              {estaAberto && (
                <div className="aval-item-corpo">
                  <label className="field aval-modelo">
                    <span>
                      Formulário {c.modelo_nome ? "(sugerido pela função)" : "— escolha a função"}
                      {c.empresa_nome ? ` · ${c.empresa_nome}` : ""}
                    </span>
                    {/* Só os formulários da EMPRESA da pessoa: o mesmo cargo
                        existe na SERCAMP e na Move Me com perguntas diferentes,
                        e o servidor recusa o envio se cruzar. */}
                    <select
                      value={modelos[c.id] ?? ""}
                      onChange={(e) => {
                        setModelos((m) => ({ ...m, [c.id]: e.target.value }));
                        tocar(c.id);
                      }}
                    >
                      <option value="">Selecione…</option>
                      {ordenarPorNome(
                        form.modelos.filter(
                          (m) => !c.empresa || (m.empresa ?? "sercamp") === c.empresa,
                        ),
                        (m) => m.nome,
                      ).map((m) => (
                          <option key={m.key} value={m.key}>
                            {m.nome} ({m.departamento})
                          </option>
                        ))}
                    </select>
                  </label>

                  <FormularioAvaliacao
                    modelo={modeloDe(c.id)}
                    niveis={form.niveis}
                    respostas={r}
                    onChange={(novo) => {
                      setRespostas((prev) => ({ ...prev, [c.id]: novo }));
                      tocar(c.id);
                    }}
                  />

                  <div className="aval-item-rodape">
                    <span className="muted">
                      Terminou este colaborador? Salve — dá para fechar a página e voltar depois
                      pelo mesmo link.
                    </span>
                    <button
                      type="button"
                      className="btn-secondary btn-sm"
                      onClick={() => void salvarUm(c.id, true)}
                      disabled={estados[c.id] === "salvando"}
                    >
                      <IconSave size={15} />{" "}
                      {estados[c.id] === "salvando" ? "Salvando…" : `Salvar ${c.nome.split(" ")[0]}`}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {avisoSalvar && <div className="form-error">{avisoSalvar}</div>}
      {erroEnvio && <div className="form-error">{erroEnvio}</div>}

      <div className="aval-foot">
        <span className="aval-contador">
          {preenchidas} de {form.colaboradores.length} preenchidos
          {salvos > 0 ? ` · ${salvos} salvo(s) no servidor` : ""}
        </span>
        <button type="button" className="btn-primary" onClick={enviar} disabled={enviando}>
          {enviando ? "Enviando…" : "Concluir e enviar ao RH"}
        </button>
      </div>
    </Casca>
  );
}
